import { Component, forwardRef } from '@angular/core';
import {
	NG_VALUE_ACCESSOR,
	ControlValueAccessor,
	FormArray,
	AbstractControl,
	FormBuilder,
	FormGroup,
} from '@angular/forms';
import { EmailAddress } from 'src/shared/api';
import { EmailAddressDialogComponent } from 'src/shared/form-models/email-address/email-address-dialog/email-address-dialog.component';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil, filter } from 'rxjs/operators';
import { emailAddressFormConfig } from 'src/shared/form-models/email-address/email-address.component';

@Component({
	selector: 'rpshared-email-address-list',
	templateUrl: './email-address-list.component.html',
	styleUrls: ['./email-address-list.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => EmailAddressListComponent),
			multi: true,
		},
	],
})
export class EmailAddressListComponent extends SelfDestruct
	implements ControlValueAccessor {
	array = new FormArray([]);

	constructor(
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	// model --> view
	writeValue(value: EmailAddress[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(emailAddressFormConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: EmailAddress[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addEmailAddress() {
		this.dialog
			.open<EmailAddressDialogComponent, never, AbstractControl>(
				EmailAddressDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				if (control.value.isPrimary) {
					this.removeAllPrimaryFlags();
				} else if (this.array.length < 1) {
					control.setValue({
						...control.value,
						...{ isPrimary: true },
					});
				}
				this.array.insert(0, control);
			});
	}

	editEmailAddress(index: number) {
		this.dialog
			.open<EmailAddressDialogComponent, EmailAddress, AbstractControl>(
				EmailAddressDialogComponent,
				{
					width: '450px',
					data: this.array.at(index).value,
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				if (control.value.isPrimary) {
					this.removeAllPrimaryFlags();
				} else if (this.array.length < 1) {
					control.setValue({
						...control.value,
						...{ isPrimary: true },
					});
				}
				this.array.setControl(index, control);
			});
	}

	private removeAllPrimaryFlags(): void {
		const emails = this.array.value as EmailAddress[];

		emails.forEach((e) => {
			e.isPrimary = false;
		});
	}
}
